// Layer 2 engine: VLAN-aware flooding, 802.1Q trunks (native VLAN, allowed
// lists), EtherChannel (LACP / PAgP / on), per-VLAN spanning tree (PVST+ /
// Rapid-PVST+) and switch virtual interfaces.
//
// Everything is *derived* from configuration on every call, so show commands,
// forwarding and validation can never disagree with the configured state.
//
// Interface naming inside a switch:
//   FastEthernet0/1 …, GigabitEthernet0/1 …   physical ports
//   Port-channel1 …                          logical bundle (created by channel-group)
//   Vlan10 …                                 SVI (created by `interface vlan 10`)
// Router subinterfaces: GigabitEthernet0/0.10 with { encap: { vlan, native } }.

import { dev, linkOf, peerOf, linkState } from './sim.js';
import { isTunnel, tunnelPeers } from './wan.js';
import { psecIngress } from './security.js';
import { logEvent } from './services.js';

export const isSvi = (n) => /^Vlan\d+$/.test(n);
export const isPo = (n) => /^Port-channel\d+$/.test(n);
export const isSubif = (n) => /\.\d+$/.test(n);
export const parentIf = (n) => n.replace(/\.\d+$/, '');
export const isPhysical = (n) => !isSvi(n) && !isPo(n) && !isSubif(n);
export const trunkAllows = (p, v) => !p.allowed || p.allowed.includes(v);
const native = (i) => i.nativeVlan || 1;

const byPort = (a, b) => {
  const na = a.match(/\d+/g).map(Number), nb = b.match(/\d+/g).map(Number);
  const ta = a.replace(/[\d/.]+/g, ''), tb = b.replace(/[\d/.]+/g, '');
  if (ta !== tb) return ta < tb ? -1 : 1;
  for (let i = 0; i < Math.max(na.length, nb.length); i++) if ((na[i] ?? -1) !== (nb[i] ?? -1)) return (na[i] ?? -1) - (nb[i] ?? -1);
  return 0;
};

// ------------------------------------------------------------ EtherChannel
const LACP = ['active', 'passive'];
const PAGP = ['desirable', 'auto'];
export function modesCompatible(a, b) {
  if (a === 'on' || b === 'on') return a === b;
  if (LACP.includes(a) && LACP.includes(b)) return a === 'active' || b === 'active';
  if (PAGP.includes(a) && PAGP.includes(b)) return a === 'desirable' || b === 'desirable';
  return false;
}
export const protocolOf = (mode) => (LACP.includes(mode) ? 'LACP' : PAGP.includes(mode) ? 'PAgP' : '-');

/** The Layer 2 settings that must match between a member and its Port-channel. */
export function l2Signature(i) {
  const trunk = i.mode === 'trunk';
  return JSON.stringify([
    i.mode, trunk ? null : i.accessVlan, trunk ? native(i) : null,
    trunk ? (i.allowed ? [...i.allowed].sort((a, b) => a - b).join(',') : 'all') : null,
  ]);
}
const poOf = (sw, m) => sw.interfaces[`Port-channel${m.channelGroup.id}`];

/** Per-member flag before group consistency: P bundled · I stand-alone · s suspended · D down */
function baseFlag(net, swId, ifc) {
  const sw = dev(net, swId);
  const m = sw.interfaces[ifc];
  const po = poOf(sw, m);
  if (m.shutdown || po?.shutdown) return { flag: 'D' };
  const l = linkOf(net, swId, ifc);
  if (linkState(net, l) === 'down') return { flag: 'D' };
  if (!po || l2Signature(m) !== l2Signature(po)) return { flag: 's' };
  const p = peerOf(l, swId, ifc);
  const pd = dev(net, p.dev);
  if (pd.kind !== 'switch') return { flag: 'I' };
  const pm = pd.interfaces[p.ifc];
  if (!pm.channelGroup || !modesCompatible(m.channelGroup.mode, pm.channelGroup.mode)) return { flag: 'I' };
  const ppo = poOf(pd, pm);
  if (!ppo || ppo.shutdown || l2Signature(pm) !== l2Signature(ppo)) return { flag: 'I' };
  return { flag: 'P', peer: `${p.dev}|${pm.channelGroup.id}` };
}

/** Final member flag, including "all members must reach the same peer bundle". */
export function memberFlag(net, swId, ifc) {
  const sw = dev(net, swId);
  const m = sw.interfaces[ifc];
  if (!m?.channelGroup) return null;
  const me = baseFlag(net, swId, ifc);
  if (me.flag !== 'P') return me.flag;
  const group = Object.keys(sw.interfaces).filter((n) => sw.interfaces[n].channelGroup?.id === m.channelGroup.id).sort(byPort);
  for (const n of group) {
    const b = n === ifc ? me : baseFlag(net, swId, n);
    if (b.flag === 'P') return b.peer === me.peer ? 'P' : 's';
  }
  return 'P';
}

export function channelMembers(net, swId, poName) {
  const sw = dev(net, swId);
  const id = +poName.replace('Port-channel', '');
  return Object.keys(sw.interfaces).filter((n) => sw.interfaces[n].channelGroup?.id === id).sort(byPort)
    .map((n) => ({ ifc: n, flag: memberFlag(net, swId, n), mode: sw.interfaces[n].channelGroup.mode }));
}
export function poStatus(net, swId, poName) {
  const po = dev(net, swId).interfaces[poName];
  if (po.shutdown) return { status: 'administratively down', protocol: 'down', connected: false };
  const up = channelMembers(net, swId, poName).some((m) => m.flag === 'P');
  return up ? { status: 'up', protocol: 'up', connected: true } : { status: 'down', protocol: 'down', connected: false };
}

// ------------------------------------------------------------ logical ports
/** Physical port → the logical port that carries its traffic (Po for bundled members), or null. */
export function logicalOf(net, swId, phys) {
  const m = dev(net, swId).interfaces[phys];
  if (!m?.channelGroup) return phys;
  const f = memberFlag(net, swId, phys);
  if (f === 'P') return `Port-channel${m.channelGroup.id}`;
  if (f === 's' || f === 'D') return null;
  return phys; // stand-alone member behaves like a normal port
}
/** Logical L2 ports of a switch (physical ports + active Port-channels). */
export function logicalPorts(net, swId) {
  const sw = dev(net, swId);
  const out = [];
  for (const n of Object.keys(sw.interfaces).filter(isPhysical).sort(byPort)) {
    const lp = logicalOf(net, swId, n);
    if (lp && !out.includes(lp)) out.push(lp);
  }
  return out;
}
/** Physical member used to transmit on a logical port. */
export function physFor(net, swId, lp) {
  if (!isPo(lp)) return lp;
  return channelMembers(net, swId, lp).find((m) => m.flag === 'P')?.ifc || null;
}
export function carries(cfg, v) {
  return cfg.mode === 'trunk' ? trunkAllows(cfg, v) : cfg.accessVlan === v;
}

// ------------------------------------------------------------ SVIs
export function sviUp(net, swId, v) {
  const sw = dev(net, swId);
  const svi = sw.interfaces[`Vlan${v}`];
  if (!svi || svi.shutdown || !sw.vlans[v]) return false;
  // Autostate: at least one L2 port in the VLAN must be up.
  return logicalPorts(net, swId).some((lp) => {
    const cfg = sw.interfaces[lp];
    if (cfg.shutdown || !carries(cfg, v)) return false;
    const p = physFor(net, swId, lp);
    return p && linkState(net, linkOf(net, swId, p)) !== 'down';
  });
}

// ------------------------------------------------------------ Spanning tree
const SPEED = (n, i) => (i && /^\d+$/.test(i.speed) ? +i.speed : n.startsWith('Gigabit') ? 1000 : 100);
function costFor(bw) {
  if (bw >= 10000) return 2;
  if (bw >= 2000) return 3;
  if (bw >= 1000) return 4;
  if (bw >= 400) return 9;
  if (bw >= 200) return 12;
  if (bw >= 100) return 19;
  return 100;
}
export function portNumber(lp) {
  const m = lp.match(/(\d+)$/);
  const n = m ? +m[1] : 0;
  if (lp.startsWith('Gigabit')) return 24 + n;
  if (isPo(lp)) return 64 + n;
  return n;
}
export function bridgePriority(sw, v) {
  return sw.stp?.priority?.[v] ?? 32768;
}

/**
 * Compute one VLAN's spanning tree across every switch that has the VLAN.
 * Returns { vlan, bridges:{sw:{...}}, ports:{'SW|port':{role,state,cost,pri,num,type}} }
 */
export function stpVlan(net, v) {
  const sws = Object.values(net.devices).filter((d) => d.kind === 'switch' && d.vlans[v]);
  const bridges = {};
  const ports = {};
  const segs = []; // {a:{sw,lp}, b:{sw,lp}}
  const bidOf = (s) => ({ num: bridgePriority(s, v) + v, prio: bridgePriority(s, v), mac: s.baseMac });
  const pid = (s, lp) => [s.interfaces[lp].stpPriority ?? 128, portNumber(lp)];
  const cost = (s, lp) => {
    const cfg = s.interfaces[lp];
    if (cfg.stpCost) return cfg.stpCost;
    if (isPo(lp)) {
      const mem = channelMembers(net, s.id, lp).filter((m) => m.flag === 'P');
      return costFor(mem.reduce((a, m) => a + SPEED(m.ifc, s.interfaces[m.ifc]), 0));
    }
    return costFor(SPEED(lp, cfg));
  };
  for (const s of sws) {
    bridges[s.id] = { ...bidOf(s), rootCost: 0, rootPort: null };
    for (const lp of logicalPorts(net, s.id)) {
      const cfg = s.interfaces[lp];
      if (cfg.shutdown || !carries(cfg, v)) continue;
      const phys = physFor(net, s.id, lp);
      const l = phys && linkOf(net, s.id, phys);
      if (!l || linkState(net, l) === 'down') continue;
      const key = `${s.id}|${lp}`;
      const [pri, num] = pid(s, lp);
      ports[key] = { sw: s.id, lp, cost: cost(s, lp), pri, num, role: 'Desg', state: 'FWD', type: cfg.portfast ? 'P2p Edge' : 'P2p', edge: true };
      const pe = peerOf(l, s.id, phys);
      const pd = dev(net, pe.dev);
      if (pd.kind !== 'switch') continue;
      ports[key].edge = false;
      if (!pd.vlans[v]) continue;
      const plp = logicalOf(net, pd.id, pe.ifc);
      if (!plp) continue;
      const pc = pd.interfaces[plp];
      if (pc.shutdown) continue;
      // 802.1Q / PVST+ consistency: native VLAN (PVID) mismatch blocks the port.
      const mine = cfg.mode === 'trunk' ? native(cfg) : cfg.accessVlan;
      const theirs = pc.mode === 'trunk' ? native(pc) : pc.accessVlan;
      const bothTrunk = cfg.mode === 'trunk' && pc.mode === 'trunk';
      if (mine !== theirs && (v === mine || v === theirs)) {
        Object.assign(ports[key], { role: 'Desg', state: 'BKN', type: 'P2p *PVID_Inc', inconsistent: true });
        continue;
      }
      const ok = bothTrunk ? trunkAllows(pc, v) : (cfg.mode === 'trunk' || pc.mode === 'trunk') ? v === mine && v === theirs : pc.accessVlan === v;
      if (!ok) continue;
      if (s.id < pd.id || (s.id === pd.id && lp < plp)) segs.push({ a: { sw: s.id, lp }, b: { sw: pd.id, lp: plp } });
    }
  }
  // Only keep segments whose far end also registered the port (symmetry).
  const live = segs.filter((g) => ports[`${g.a.sw}|${g.a.lp}`] && ports[`${g.b.sw}|${g.b.lp}`] && !ports[`${g.b.sw}|${g.b.lp}`].inconsistent);
  const nbr = {};
  for (const g of live) {
    (nbr[g.a.sw] = nbr[g.a.sw] || []).push({ me: g.a.lp, sw: g.b.sw, lp: g.b.lp });
    (nbr[g.b.sw] = nbr[g.b.sw] || []).push({ me: g.b.lp, sw: g.a.sw, lp: g.a.lp });
  }
  const cmpBid = (x, y) => (x.num - y.num) || (x.mac < y.mac ? -1 : x.mac > y.mac ? 1 : 0);
  const cmp = (a, b) => { for (let i = 0; i < a.length; i++) { const d = typeof a[i] === 'object' ? cmpBid(a[i], b[i]) : a[i] < b[i] ? -1 : a[i] > b[i] ? 1 : 0; if (d) return d; } return 0; };
  // Connected components elect their own root.
  const comp = {};
  for (const s of sws) {
    if (comp[s.id]) continue;
    const stack = [s.id];
    const members = [];
    comp[s.id] = s.id;
    while (stack.length) { const x = stack.pop(); members.push(x); for (const n of nbr[x] || []) if (!comp[n.sw]) { comp[n.sw] = s.id; stack.push(n.sw); } }
    const root = members.map((id) => ({ id, b: bridges[id] })).sort((p, q) => cmpBid(p.b, q.b))[0].id;
    for (const id of members) Object.assign(bridges[id], { root, rootCost: id === root ? 0 : Infinity });
  }
  // Bellman-Ford with 802.1D tie-breakers: cost, sender BID, sender port ID, own port ID.
  for (let iter = 0; iter < sws.length + 2; iter++) {
    for (const s of sws) {
      const B = bridges[s.id];
      if (B.root === s.id) continue;
      let best = null;
      for (const n of nbr[s.id] || []) {
        const nb = bridges[n.sw];
        if (!Number.isFinite(nb.rootCost)) continue;
        if (nb.rootPort === n.lp) continue; // a neighbour never advertises out of its own root port as better
        const cand = { key: [nb.rootCost + ports[`${s.id}|${n.me}`].cost, nb, ports[`${n.sw}|${n.lp}`].pri, ports[`${n.sw}|${n.lp}`].num, ports[`${s.id}|${n.me}`].pri, ports[`${s.id}|${n.me}`].num], port: n.me };
        if (!best || cmp(cand.key, best.key) < 0) best = cand;
      }
      if (best) { B.rootCost = best.key[0]; B.rootPort = best.port; }
    }
  }
  // Roles on each segment.
  for (const s of sws) {
    const B = bridges[s.id];
    if (B.rootPort) Object.assign(ports[`${s.id}|${B.rootPort}`], { role: 'Root', state: 'FWD' });
  }
  for (const g of live) {
    const A = ports[`${g.a.sw}|${g.a.lp}`], Bp = ports[`${g.b.sw}|${g.b.lp}`];
    if (A.role === 'Root' || Bp.role === 'Root') continue;
    const ka = [bridges[g.a.sw].rootCost, bridges[g.a.sw], A.pri, A.num];
    const kb = [bridges[g.b.sw].rootCost, bridges[g.b.sw], Bp.pri, Bp.num];
    const loser = cmp(ka, kb) <= 0 ? Bp : A;
    Object.assign(loser, { role: 'Altn', state: 'BLK' });
  }
  for (const id of Object.keys(bridges)) {
    const B = bridges[id];
    B.rootBridge = bridges[B.root];
    B.isRoot = B.root === id;
  }
  return { vlan: v, bridges, ports };
}

// ------------------------------------------------------------ flooding
function routerIfFor(d, phys, tag) {
  const subs = Object.entries(d.interfaces).filter(([n, i]) => parentIf(n) === phys && n !== phys && i.encap && !i.shutdown);
  if (tag === null) return subs.find(([, i]) => i.encap.native)?.[0] || phys;
  return subs.find(([, i]) => i.encap.vlan === tag && !i.encap.native)?.[0] || null;
}

/**
 * Flood a frame from an L3 endpoint interface (host NIC, router interface or
 * subinterface, switch SVI) through the switched fabric. Returns every L3
 * endpoint that receives it with the switch hops traversed.
 * hop = { sw, inPort, outPort, vlan }  (inPort/outPort are logical ports; null at an SVI)
 */
export function l2Flood(net, startId, startIfc) {
  if (isTunnel(startIfc)) return tunnelPeers(net, startId, startIfc);
  const out = [];
  const stp = {};
  const fwd = (S, lp, v) => {
    const t = (stp[v] = stp[v] || stpVlan(net, v));
    const p = t.ports[`${S}|${lp}`];
    return !p || p.state === 'FWD';
  };
  const d0 = dev(net, startId);
  const q = [];
  const seen = new Set();
  if (d0.kind === 'switch' && isSvi(startIfc)) {
    q.push({ inside: true, sw: startId, v: +startIfc.slice(4), inPort: null, hops: [], err: [] });
  } else {
    const phys = parentIf(startIfc);
    const sub = d0.interfaces?.[startIfc];
    const tag = isSubif(startIfc) && sub?.encap && !sub.encap.native ? sub.encap.vlan : null;
    const l0 = linkOf(net, startId, d0.kind === 'pc' || d0.kind === 'server' ? startIfc : phys);
    const s0 = linkState(net, l0);
    if (s0 === 'down') return out;
    const first = peerOf(l0, startId, l0.a.dev === startId ? l0.a.ifc : l0.b.ifc);
    // Port security: a host's frame entering a secured switch access port.
    if ((d0.kind === 'pc' || d0.kind === 'server') && dev(net, first.dev).kind === 'switch') {
      const sw = dev(net, first.dev);
      const r = psecIngress(sw, first.ifc, d0.nic.mac, sw.interfaces[first.ifc]?.accessVlan || 1);
      for (const t of r.logs) { logEvent(net, first.dev, t); (sw.consoleQueue ||= []).push(t); }
      if (!r.pass) return out;
    }
    q.push({ at: first, tag, hops: [], err: s0 === 'errors' ? [l0.id] : [] });
  }
  while (q.length) {
    const it = q.shift();
    if (!it.inside) {
      const { at, tag, hops, err } = it;
      const d = dev(net, at.dev);
      if (d.kind !== 'switch') {
        let ifc = at.ifc;
        if (d.kind === 'router') { ifc = routerIfFor(d, at.ifc, tag); if (!ifc) continue; } else if (tag !== null) continue;
        out.push({ dev: at.dev, ifc, hops, errLinks: err });
        continue;
      }
      const lp = logicalOf(net, at.dev, at.ifc);
      if (!lp) continue;
      const cfg = d.interfaces[lp];
      let v;
      if (cfg.mode === 'trunk') {
        v = tag ?? native(cfg);
        if (!trunkAllows(cfg, v)) continue;
      } else {
        if (tag !== null) continue; // tagged frame on an access port is dropped
        v = cfg.accessVlan;
      }
      if (!d.vlans[v]) continue;
      if (!fwd(at.dev, lp, v)) continue;
      q.push({ inside: true, sw: at.dev, v, inPort: lp, hops, err });
      continue;
    }
    const { sw, v, inPort, hops, err } = it;
    const key = `${sw}|${v}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const d = dev(net, sw);
    if (inPort !== null && d.interfaces[`Vlan${v}`] && sviUp(net, sw, v)) {
      out.push({ dev: sw, ifc: `Vlan${v}`, hops: [...hops, { sw, inPort, outPort: null, vlan: v }], errLinks: err });
    }
    for (const lp of logicalPorts(net, sw)) {
      if (lp === inPort) continue;
      const cfg = d.interfaces[lp];
      if (cfg.shutdown) continue;
      let outTag;
      if (cfg.mode === 'trunk') {
        if (!trunkAllows(cfg, v)) continue;
        outTag = v === native(cfg) ? null : v;
      } else {
        if (cfg.accessVlan !== v) continue;
        outTag = null;
      }
      if (!fwd(sw, lp, v)) continue;
      const phys = physFor(net, sw, lp);
      if (!phys) continue;
      const l = linkOf(net, sw, phys);
      const st = linkState(net, l);
      if (st === 'down') continue;
      q.push({
        at: peerOf(l, sw, phys), tag: outTag,
        hops: [...hops, { sw, inPort, outPort: lp, vlan: v }],
        err: st === 'errors' ? [...err, l.id] : err,
      });
    }
  }
  return out;
}
